import React, { createContext, useContext, useState, useEffect } from "react";
import { GetOrderGroups } from "../../services/backendServices";
import { ORDER_GROUP_STATUS, ODRER_MANAGER_COMBINED_TARGET_SL_STATUS, STORAGE_KEYS } from "../../Util/key_helper";
import { toast } from "react-toastify";
// Create a context for Order Management
const OrderManagerContext = createContext();

/*** OrderManagerProvider: A provider component to manage and supply order data */
export const OrderManagerProvider = ({ children }) => {
    const [orderGroupData, setOrderGroupData] = useState([]);
    const [isOrderGroupDataLoading, setIsOrderGroupDataLoading] = useState(false)
    const [isOrderGroupDataFetched, setIsOrderGroupFetched] = useState(false)
    // Function to count open positions and determine button visibility for a specific group
    const getOrderGroupStatus = (group_id) => {
        // Find the order group from the provided orderGroupData based on the given group_id
        const group = orderGroupData.find(order => order.og_id === group_id);
        if (!group) return { openPositionCount: 0, showExitAllPosButton: false, showCancelAllButton: false };
        let openCount = 0; // Counter for open positions
        let hasOpen = false;  // Flag to check if there are any open positions
        let hasWaiting = false; // Flag to check if there are any waiting positions
        // Iterate through the position_data array of the group (if available)
        group.position_data?.forEach(position => {
            // If the position status is OPEN, increment the open position count and set hasOpen to true
            if (position.op_status === ORDER_GROUP_STATUS.OPEN) {
                hasOpen = true;
                openCount++;
            }
            // If the position status is WAITING, set hasWaiting to true
            if (position.op_status === ORDER_GROUP_STATUS.WAITING) {
                hasWaiting = true;
            }
        });
        // Check whether at least one position is closed or otherwise not active.
        const hasClosedPosition = group?.position_data?.some((position) => {
            return position?.op_status !== ORDER_GROUP_STATUS.OPEN && position?.op_status !== ORDER_GROUP_STATUS.WAITING;
        }) ?? false;
        // Return an object containing:
        // - The total number of open positions
        // - A flag indicating whether to show the "Exit All Positions" button
        // - A flag indicating whether to show the "Cancel All" button
        return {
            openPositionCount: openCount,
            showExitAllPosButton: hasOpen,
            showCancelAllButton: hasWaiting,
            showExecuteAllButton: hasWaiting,
            showDeleteAllButton: hasClosedPosition
        };
    };;

    // Function  to  order group data
    const fetchOrderGroupdata = async () => {
        setIsOrderGroupDataLoading(true)
        try {
            const res = await GetOrderGroups();
            if (res.status) {
                setOrderGroupData(res.data);
                setIsOrderGroupFetched(true); // Mark data as fetched
            }
        } catch (e) {
            console.error("Error fetching Order Group Data:", e);
        } finally {
            setIsOrderGroupDataLoading(false); // End loading
        }
    };

    // Function to add a new order group
    const addOrderGroup = (newOrderGroup) => {
        setOrderGroupData((prevData) => [...prevData, newOrderGroup]);
    };
    // Function to add a new position to an existing order group
    const addPositionToOrderGroup = (og_id, newPosition) => {
        setOrderGroupData((prevData) =>
            prevData.map((group) => {
                if (group.og_id === og_id) {
                    const positions = group.position_data ?? [];
                    // Check if the position with the given op_id already exists
                    const positionExists = positions.some(pos => pos.op_id === newPosition.op_id);
                    if (positionExists) {
                        return group; // No changes if the position already exists
                    }
                    //if response comes as `pnl`, store it as `op_pnl`
                    const op_pnl = Number(newPosition?.pnl) || 0;
                    // og_pnl = sum of all previous op_pnl + new position's op_pnl
                    const prevPnl = positions.reduce((sum, pos) => sum + (Number(pos?.op_pnl) || 0), 0);
                    const og_pnl = prevPnl + op_pnl;
                    const updatedPosition = { ...newPosition, op_pnl, og_pnl };
                    // Add new position if it does not exist
                    return { ...group, position_data: [...positions, updatedPosition] };
                }
                return group;
            })
        );
    };


    // Function to update an existing position inside an order group
    const updatePositionInOrderGroup = (op_id, updatedFields) => {
        setOrderGroupData((prevData) =>
            prevData.map((group) => ({
                ...group,
                position_data: group.position_data.map((position) =>
                    position.op_id === op_id
                        ? { ...position, ...updatedFields }
                        : position
                ),
            }))
        );
    };
    // Function to add or update a position in an order group
    const addOrUpdatePositionInOrderGroup = (og_id, op_id, positionData) => {
        setOrderGroupData((prevData) =>
            prevData.map((group) => {
                if (group.og_id === og_id) {
                    const positions = group.position_data ?? [];
                    const existingPositionIndex = positions.findIndex(pos => pos.op_id === op_id);
                    let updatedPositions;
                    if (existingPositionIndex !== -1) {
                        // Update existing position
                        updatedPositions = positions.map(pos =>
                            pos.op_id === op_id ? { ...pos, ...positionData } : pos
                        );
                    } else {
                        updatedPositions = [...positions, positionData];
                    }
                    // Check for GROUP_SL or GROUP_TGT
                    const shouldDisableOrder = updatedPositions.some(pos =>
                        pos.op_status === ODRER_MANAGER_COMBINED_TARGET_SL_STATUS.GROUP_TGT || pos.op_status === ODRER_MANAGER_COMBINED_TARGET_SL_STATUS.GROUP_SL
                    );
                    return {
                        ...group, position_data: updatedPositions, og_place_order: shouldDisableOrder ? false : group.og_place_order,
                    };
                }
                return group;
            })
        );
    };
    // Function to update fields of a specific Order Group by og_id
    const updateOrderGroup = (og_id, updatedFields) => {
        setOrderGroupData((prevData) =>
            prevData.map((group) =>
                group.og_id === og_id
                    ? { ...group, ...updatedFields }
                    : group
            )
        );
    };
    // Function to remove an Order Group by og_id
    const removeOrderGroup = (og_id) => {
        setOrderGroupData((prevData) =>
            prevData.filter((group) => group.og_id !== og_id) // Filter out the group with the matching og_id
        );
    };
    // Function to remove a position by op_id across all order groups
    const removePositionByOpId = (op_id) => {
        setOrderGroupData((prevData) =>
            prevData.map((group) => {
                const positions = group.position_data ?? [];
                if (!positions.some((position) => position.op_id === op_id)) return group;
                const remaining = positions.filter((position) => position.op_id !== op_id);
                const og_pnl = remaining.reduce((sum, pos) => sum + (Number(pos?.op_pnl) || 0), 0);
                return {
                    ...group,
                    position_data: remaining.map((pos) => ({ ...pos, og_pnl })),
                };
            })
        );
    };
    // Function To handle refresh Group Data 
    const handelRefreshOrderGroupData = async () => {
        try {
            await fetchOrderGroupdata();
            toast.success("Data refreshed successfully!");
        } catch (error) {
            toast.error("Error refreshing data. Please try again.");
        }
    };
    useEffect(() => {
        const handleLoginStatusChange = (event) => {
            // Check if the specific key changed
            if (event.key === STORAGE_KEYS.BROKER_PANEL_USER_IS_LOGGIN_IN) {
                // Reset Order Group State
                setIsOrderGroupFetched(false);
            }
        };

        // Listen for storage changes (cross-tab)
        window.addEventListener("storage", handleLoginStatusChange);

        return () => {
            window.removeEventListener("storage", handleLoginStatusChange);
        };
    }, []);
    return (
        <OrderManagerContext.Provider
            value={{
                fetchOrderGroupdata, // provides the function to fetch order group data 
                orderGroupData, // provides the current order group data 
                isOrderGroupDataLoading, // provides the state to order group data loading state
                isOrderGroupDataFetched, // provides  the fetched status of order group data 
                setIsOrderGroupFetched, // provides the state to chnage the fetched data state 
                addOrderGroup, // provides the function to add order group data 
                addPositionToOrderGroup, // provides the function to add position into group
                updatePositionInOrderGroup, // rovides the function to update position in group
                updateOrderGroup, // provide the function to update order group data 
                removePositionByOpId, // provides the function to remove position 
                removeOrderGroup, // provides the functoin to remove grou
                getOrderGroupStatus, // Function to get open position count & button visibility for a specific group
                addOrUpdatePositionInOrderGroup, // provides the function to add update position in order group
                handelRefreshOrderGroupData  // provides the function to refresh the order group data 
            }}>
            {children}
        </OrderManagerContext.Provider>
    );
};

// Custom hook to use the OrderManagerContext
export const UseOrderManagerContext = () => useContext(OrderManagerContext);
